/* CarbonERA Instagram carousel — 5-second single-card autoplay.
 * Rotates existing cards rather than cloning Instagram iframes.
 * Supports swipe/drag, pause, focus, visibility and Shopify theme editor lifecycle. */
(() => {
  'use strict';
  const instances = new WeakMap();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const slideDelay = 5000;
  const findCarousel = (context) => {
    const result = [];
    if (context?.matches?.('.ce-instagram')) result.push(context);
    context?.querySelectorAll?.('.ce-instagram').forEach(el => result.push(el));
    return result;
  };
  function initialize(section) {
    if (instances.has(section)) return;
    const track = section.querySelector('[data-ce-ig-track]');
    const toggle = section.querySelector('[data-ce-ig-toggle]');
    const previousButton = section.querySelector('[data-ce-ig-prev]');
    const nextButton = section.querySelector('[data-ce-ig-next]');
    if (!track || !toggle || !previousButton || !nextButton || track.children.length < 2) return;
    const controller = new AbortController();
    const { signal } = controller;
    let timer = null, finishTimer = null, scrollTimer = null, observer = null;
    // Autoplay also works in the theme editor when the Instagram section isn't selected.
    // Hover alone is not a user interaction; visitors can always use Pause autoplay.
    let userPaused = reduceMotion.matches;
    let focused = false, interacting = false, editorSelected = false;
    let inView = !('IntersectionObserver' in window);
    let advancing = false, destroyed = false;
    const playable = () => !destroyed && !userPaused && !focused &&
       !interacting && !editorSelected && inView && !document.hidden;
    function paintButton() {
      toggle.disabled = editorSelected;
      if (editorSelected) {
        toggle.textContent = 'Paused while editing';
        toggle.setAttribute('aria-label', 'Instagram autoplay paused while editing this section');
        toggle.setAttribute('aria-pressed', 'true');
        return;
      }
      toggle.setAttribute('aria-pressed', String(userPaused));
      toggle.setAttribute('aria-label', userPaused ? 'Resume Instagram autoplay' : 'Pause Instagram autoplay');
      toggle.innerHTML = userPaused ? 'Resume autoplay <span aria-hidden="true">▶</span>' :
         'Pause autoplay <span aria-hidden="true">Ⅱ</span>';
    }
    function clearScheduled() {
      clearTimeout(timer);
      clearTimeout(finishTimer);
      clearTimeout(scrollTimer);
      timer = finishTimer = scrollTimer = null;
    }
    function cardStep() {
      const first = track.firstElementChild;
      if (!first) return 0;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return first.getBoundingClientRect().width + gap;
    }
    /* Once a full card is past the left edge, append it behind the others
       and remove its consumed scroll offset. Visible cards never get moved. */
    function normalize() {
      if (destroyed || track.children.length < 2) return;
      const scroll = track.scrollLeft;
      if (scroll < cardStep() - 3) return;
      track.classList.add('ce-ig-adjusting');
      const behavior = track.style.scrollBehavior;
      const anchor = track.style.overflowAnchor;
      track.style.scrollBehavior = 'auto';
      track.style.overflowAnchor = 'none';
      let rotations = 0;
      while (rotations < track.children.length - 1) {
        const step = cardStep();
        if (!step || track.scrollLeft < step - 3) break;
        const left = track.scrollLeft;
        const first = track.firstElementChild;
        track.appendChild(first);
        track.scrollLeft = Math.max(0, left - step);
        rotations += 1;
      }
      requestAnimationFrame(() => {
        if (destroyed) return;
        track.style.scrollBehavior = behavior;
        track.style.overflowAnchor = anchor;
        track.classList.remove('ce-ig-adjusting');
      });
    }
    function schedule() {
      clearTimeout(timer);
      timer = null;
      paintButton();
      if (!playable() || advancing) return;
      timer = setTimeout(advance, slideDelay);
    }
    function advance(manual = false) {
      timer = null;
      if ((!manual && !playable()) || advancing) return schedule();
      normalize();
      const step = cardStep();
      if (!step || track.scrollWidth <= track.clientWidth + 4) return schedule();
      advancing = true;
      track.scrollBy({ left: step, behavior: 'smooth' });
      finishTimer = setTimeout(() => {
        finishTimer = null;
        advancing = false;
        normalize();
        schedule();
      }, 950);
    }
    function movePrevious() {
      clearTimeout(timer);
      timer = null;
      if (advancing) return;
      normalize();
      const step = cardStep();
      if (!step || track.scrollWidth <= track.clientWidth + 4) return schedule();
      advancing = true;
      if (track.scrollLeft <= 3) {
        // Insert the last existing card before the first, then visually scroll
        // backwards one card. No duplicate Instagram embeds are created.
        track.classList.add('ce-ig-adjusting');
        const previousBehavior = track.style.scrollBehavior;
        const previousSnap = track.style.scrollSnapType;
        track.style.scrollBehavior = 'auto';
        track.style.scrollSnapType = 'none';
        track.insertBefore(track.lastElementChild, track.firstElementChild);
        track.scrollLeft = step;
        requestAnimationFrame(() => {
          if (destroyed) return;
          track.style.scrollBehavior = previousBehavior;
          track.style.scrollSnapType = previousSnap;
          track.classList.remove('ce-ig-adjusting');
          track.scrollBy({ left: -step, behavior: reduceMotion.matches ? 'instant' : 'smooth' });
        });
      } else {
        track.scrollBy({ left: -step, behavior: reduceMotion.matches ? 'instant' : 'smooth' });
      }
      finishTimer = setTimeout(() => {
        finishTimer = null;
        advancing = false;
        normalize();
        schedule();
      }, reduceMotion.matches ? 120 : 950);
    }
    previousButton.addEventListener('click', movePrevious, { signal });
    nextButton.addEventListener('click', () => {
      clearTimeout(timer);
      advance(true);
    }, { signal });
    function onManualScroll() {
      if (advancing) return;
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => { scrollTimer = null; normalize(); schedule(); }, 260);
    }
    toggle.addEventListener('click', () => {
      userPaused = !userPaused;
      clearTimeout(timer);
      schedule();
    }, { signal });
    // Do not pause simply because the pointer is parked over the section.
    // For keyboard accessibility, pause only while keyboard-visible focus is inside it.
    const updateFocus = () => {
      const active = document.activeElement;
      focused = Boolean(active && section.contains(active) && active.matches(':focus-visible'));
      if (focused) clearTimeout(timer);
      else schedule();
    };
    section.addEventListener('focusin', () => requestAnimationFrame(updateFocus), { signal });
    section.addEventListener('focusout', () => requestAnimationFrame(updateFocus), { signal });
    track.addEventListener('pointerdown', () => { interacting = true; clearTimeout(timer); }, { signal });
    const endPointer = () => { interacting = false; schedule(); };
    // Release interaction pauses even when a drag ends outside the track.
    window.addEventListener('pointerup', endPointer, { signal });
    window.addEventListener('pointercancel', endPointer, { signal });
    track.addEventListener('scroll', onManualScroll, { passive: true, signal });
    document.addEventListener('visibilitychange', schedule, { signal });
    reduceMotion.addEventListener('change', () => {
      if (reduceMotion.matches) userPaused = true;
      schedule();
    }, { signal });
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => {
        inView = entries[0]?.isIntersecting || false;
        schedule();
      }, { threshold: 0.08 });
      observer.observe(track);
    }
    paintButton();
    schedule();
    instances.set(section, {
      destroy() {
        destroyed = true;
        clearScheduled();
        observer?.disconnect();
        controller.abort();
        instances.delete(section);
      },
      pauseSelection() {
        editorSelected = true;
        schedule();
      },
      resumeSelection() {
        editorSelected = false;
        schedule();
      }
    });
    if (window.instgrm?.Embeds?.process) window.instgrm.Embeds.process();
  }
  function initAll(context = document) { findCarousel(context).forEach(initialize); }
  function start() { initAll(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', ev => initAll(ev.target));
  document.addEventListener('shopify:section:unload', ev => findCarousel(ev.target).forEach(section => instances.get(section)?.destroy()));
  document.addEventListener('shopify:section:select', ev => findCarousel(ev.target).forEach(section => instances.get(section)?.pauseSelection()));
  document.addEventListener('shopify:section:deselect', ev => findCarousel(ev.target).forEach(section => instances.get(section)?.resumeSelection()));
})();
